import { autobind } from "core-decorators";

import { Inject } from "typescript-ioc";
import { UiComponent } from "Ui/Scripts/UiComponent";
import { UiComponentFactory } from "Ui/Scripts/UiComponentFactory";
import { EventBinder } from "Events/Scripts/EventBinder";
import { LoggerFactory } from "Logging/Scripts/LoggerFactory";
import { DeliveryChannelsHelper } from "./DeliveryChannelsHelper";
import { HomeDeliveryAddressKey } from "./HomeDeliveryAddressKey";

import { HintMessage } from "./HintMessage";

// Channels
import { SpoDeliveryChannel } from "./SpoDeliveryChannel";
import { DefaultDeliveryChannel } from "./DefaultDeliveryChannel";
import { PickupPointDeliveryChannel } from "./PickupPointDeliveryChannel";
import { MultipleCouriersDeliveryChannel } from "./MultipleCouriersDeliveryChannel";
import { ShippingOffer } from "./ShippingOffer/ShippingOffer";
import { MultipleCouriersToFriendChannel } from "./MultipleCouriersToFriendChannel";

// Utils
import { Utils } from "Helpers/Scripts/Utils";
import * as DeliveryChannelsEventType from "./DeliveryChannelsEventType";
import { FetchService } from "Async/Scripts/FetchService";
import { Spinner } from "Spinner/Scripts/Spinner";

// Interfaces
import { IMapModule } from "Map/Scripts/IMapModule";
import { IDeliveryComponent } from "./IDeliveryComponent";
import { IDeliveryModel } from "./IDeliveryModel";
import { IDeliveryUrlLinks } from "./IDeliveryUrlLinks";
import { IAjaxError } from "./IAjaxError";
import { IChannelUpdateEvent } from "./IChannelUpdateEvent";
import { ISelectedChannelAddress } from "./ISelectedChannelAddress";
import { IAllDeliveryChannelsData } from "./IAllDeliveryChannelsData";
import { IPickupPointModel } from "./IPickupPointModel";
import { IMultipleCouriersModel } from "./IMultipleCouriersModel";
import { INotificationsPhones } from "./INotificationsPhones";
import { IMultipleCouriersToFriendModel } from "./IMultipleCouriersToFriendModel";
import { IExtendedChannelsInfo } from "./IExtendedChannelsInfo";

import "../Styles/deliverychannel.css";
import "../Styles/deliverymessage.css";

export class DeliveryChannels extends UiComponent {
    private _isInited: boolean = false;
    private _isCheckoutButtonDisabled: boolean = false;
    private readonly _fetchService: FetchService;
    private _shippingOffer: ShippingOffer | null;
    private _hintMessage: HintMessage | null;
    private _deliveryHelper: DeliveryChannelsHelper;
    // TODO: set proper naming => "delivery" instead of "offers"
    private readonly SHOPPING_BAG_WRAPPER: string = ".js-w-shopping-bag";
    private readonly CHECKOUT_BUTTON_DELIVERY: string = ".js-btn-checkout.js-checkout-offer";
    private readonly NO_DELIVERY_CHANNEL_AVAILABLE: string = "js-no-delivery-channel-available";
    private readonly DISABLED: string = "k-state-disabled";
    private readonly DELIVERY_CHANNELS_LIST: string = ".js-delivery-channels-list";
    private readonly SELECTED_ADDRESS_ID_DATA_KEY: string = "selectedAddressId";
    private readonly END_CUSTOMER_ADDRESS_ID: number = 0;
    private readonly SINGLE_DELIVERY_METHOD: number = 1;
    private readonly DELIVERY_METHOD_TYPE_IS_NOT_SPECIFIED: string = "";

    // Urls to fetch data from the API end points
    private _getDeliveryModelUrl: string;
    private _updateDeliveryChannelUrl: string;
    private _updateDeliveryAddressUrl: string;
    private _ajaxErrorTitle: string;
    private _ajaxErrorMessage: string;
    private _mapModule: IMapModule;

    private _channels: IDeliveryComponent[];
    private _pickupPoint: PickupPointDeliveryChannel | undefined;
    private _selected: ISelectedChannelAddress;
    private _savedSelectedDeliveryChannel: IChannelsInfo | null;
    private _deliveryToFriendComponent?: MultipleCouriersToFriendChannel;

    public get key(): string {
        return "DeliveryChannels";
    }

    constructor(
        @Inject componentFactory: UiComponentFactory,
        @Inject binder: EventBinder,
        @Inject loggerFactory: LoggerFactory,
        @Inject fetchService: FetchService,
        @Inject deliveryHelper: DeliveryChannelsHelper
    ) {
        super(componentFactory, binder, loggerFactory);

        this._fetchService = fetchService;
        this._channels = [];
        this._shippingOffer = null;
        this._hintMessage = null;
        this._selected = {
            deliveryAddress: null,
            deliveryChannel: null,
        };
        this._deliveryHelper = deliveryHelper;
    }

    public init(): void {
        this._setOptions();
        this._bindEvents();
    }

    private _setOptions(): void {
        this._logger.info("Setting delivery options from data attributes...");

        const {
            getDeliveryModel = "",
            updateDeliveryChannel = "",
            updateDeliveryAddress = "",
        }: IDeliveryUrlLinks = this.getData("url");

        this._getDeliveryModelUrl = getDeliveryModel;
        this._updateDeliveryChannelUrl = updateDeliveryChannel;
        this._updateDeliveryAddressUrl = updateDeliveryAddress;

        const {
            ajaxErrorTitle = "Error",
            ajaxErrorMessage = "An error occured during request, please try again later.",
        }: IAjaxError = this.getData("ajax-error");

        this._ajaxErrorTitle = ajaxErrorTitle;
        this._ajaxErrorMessage = ajaxErrorMessage;

        this._mapModule = this.getData("map-module");
    }

    private _bindEvents(): void {
        this._bindStepChange();
        this._binder.bindCustomEvent(DeliveryChannelsEventType.CHANNEL_UPDATE, this._channelUpdate);
    }

    private _bindStepChange(): void {
        if (!ori) {
            this._logger.error("Ori namespace is not available");
            return;
        }

        if (!ori.shopping || !ori.shopping.wizard) {
            this._logger.error("Shopping wizard is not available");
            return;
        }

        ori.shopping.wizard.on(
            ori.shopping.wizard.shoppingContextChangeEvent(),
            this._handleShoppingContextChange
        );
    }

    @autobind
    private _handleShoppingContextChange(
        _: any,
        shoppingContext: IShoppingContext,
        currentPageId: number,
        _senderPageId: number,
        preventReloadModules?: boolean
    ): void {
        if (
            ori.shopping &&
            ori.shopping.wizard &&
            currentPageId !== ori.shopping.wizard.deliveryPageId
        ) {
            return;
        }
        // the deliveryModel will be empty only first time so all initialization goes here
        if (!this._isInited) {
            this._addSpinner();
        }
        // checkout button element is rendered after module initialization and re-rendered every time shopping context changes
        this._bindCheckoutButtonClick();

        if (preventReloadModules) {
            return;
        }
        this._fetchService.get(this._getDeliveryModelUrl).then(
            (data: IDeliveryModel) => {
                this._logger.info("Delivery model fetched successfully.");

                // set data attributes for shipping offers
                this._setCouriersDataAttributes(data);

                this._savedSelectedDeliveryChannel = data.SelectedDeliveryChannel;

                if (!this._isInited) {
                    // Save the selected delivery channel so it can be used on other places
                    this._selected.deliveryChannel = null;
                    if (shoppingContext.IsDeliveryChannelSelected) {
                        this._selected.deliveryChannel = shoppingContext.DeliveryChannelId;
                    }

                    this._initHintMessage(data);

                    this._initAllChannels(data);

                    // Init the shipping offer
                    if (
                        data.SelectedDeliveryChannel &&
                        data.SelectedDeliveryChannel.ShippingOffer
                    ) {
                        this._handleInitOfShippingOffer(
                            data.SelectedDeliveryChannel,
                            data.IsDeliveryToFriendEnabled
                        );

                        this._initShippingOffer(data.SelectedDeliveryChannel);
                    }
                    this._isInited = true;
                    this._removeSpinner();
                } else {
                    this._updateAllChannels(data);
                    if (
                        data.SelectedDeliveryChannel &&
                        data.SelectedDeliveryChannel.ShippingOffer
                    ) {
                        this._updateShippingOffer(data.SelectedDeliveryChannel);
                    }
                }
                this._updateHintMessage(data);

                if (this._selected.deliveryChannel === null) {
                    this._disableCheckoutButton();
                }
            },
            (error) => {
                this._logger.error("Delivery model fetch failed - %s", error);
                this._removeSpinner();
                if (ori.shopping) {
                    this.context.innerHTML = ori.shopping.getAjaxErrorMessage();
                } else {
                    this.context.innerHTML = "";
                }
            }
        );
    }

    private _setCouriersDataAttributes(data: IDeliveryModel): void {
        this.setData(this.SELECTED_ADDRESS_ID_DATA_KEY, data.SelectedHomeDeliveryAddress);
    }

    private _handleInitOfShippingOffer(
        selectedDeliveryChannel: IChannelsInfo,
        isCouriersDeliveryToFriendEnabled: boolean
    ): void {
        this._initShippingOffer(selectedDeliveryChannel);

        // EndConsumerSelectorComponent (part of MultipleCouriersToFriendChannel) was written asynchronously
        // and shipping offers renders before the EndConsumerSelectorComponent is rendered.
        // That's why I need to pass instance of shipping offer to MultipleCouriersToFriendChannel and update shipping offers
        if (isCouriersDeliveryToFriendEnabled && this._shippingOffer) {
            const couriersToFriendChannel = this._channels.find(
                (channel) => channel.key === MultipleCouriersToFriendChannel.componentName
            ) as MultipleCouriersToFriendChannel;
            if (couriersToFriendChannel) {
                couriersToFriendChannel.shippingOffer = this._shippingOffer;
            }
        }
    }

    private _initShippingOffer(selectedDeliveryChannel: IChannelsInfo): void {
        this._shippingOffer = this.createComponent(
            ShippingOffer,
            undefined,
            selectedDeliveryChannel
        );
    }

    private _updateShippingOffer(selectedDeliveryChannel: IChannelsInfo): void {
        if (this._shippingOffer) {
            this._shippingOffer.update(selectedDeliveryChannel);
        } else if (selectedDeliveryChannel && !this._shippingOffer) {
            this._initShippingOffer(selectedDeliveryChannel);
        }
    }

    private _initHintMessage(data: IDeliveryModel): void {
        if (!data) {
            return;
        }
        this._hintMessage = this.createComponent(HintMessage, undefined, data);
    }

    private _updateHintMessage(data: IDeliveryModel): void {
        if (this._hintMessage) {
            this._hintMessage.update(data);
        }
    }

    @autobind
    private _channelUpdate(event: IChannelUpdateEvent): void {
        event.stopPropagation();

        if (event.deliveryChannel) {
            this._selected.deliveryChannel = event.deliveryChannel;
            this._handleChannelUpdate(this._selected.deliveryChannel, event.preventReloadModules);
        }

        if (
            (event.deliveryAddress || event.deliveryAddress === this.END_CUSTOMER_ADDRESS_ID) &&
            this._selected.deliveryAddress !== event.deliveryAddress
        ) {
            this._selected.deliveryAddress = event.deliveryAddress;
            this._handleAddressUpdate(this._selected.deliveryAddress, event.preventReloadModules);
        }

        if (
            (!event.deliveryChannel && !event.deliveryAddress) ||
            (event.isCourierSelection && !event.deliveryChannel)
        ) {
            this._disableCheckoutButton();
            this._selected.deliveryChannel = null;

            if (this._shippingOffer) {
                this._shippingOffer.hideShippingOfferBar();
            }
        } else if (this._isCheckoutButtonDisabled) {
            this._enableCheckoutButton();
        }
    }

    /**
     * Binds event handler for "Continue" button click.
     * For SuperApp on iOS this needs to be handled in a different way as Order summary box is moved out of the delivery page due to
     * iframe issues within app.
     */
    private _bindCheckoutButtonClick(): void {
        const handler = (event: JQuery.TriggeredEvent) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            const clickedButton = event.target;
            const clickedButtonWrapper = event.target.parentElement;
            if (
                clickedButton.classList.contains(this.DISABLED) ||
                (clickedButtonWrapper && clickedButtonWrapper.classList.contains(this.DISABLED))
            ) {
                return;
            }

            if (this._savedSelectedDeliveryChannel) {
                const shippingOffer = this._savedSelectedDeliveryChannel.ShippingOffer;

                if (shippingOffer?.ShippingOfferExists && !shippingOffer.ShippingOfferApplied) {
                    if (this._shippingOffer) {
                        this._shippingOffer.shippingOfferBeforeCheckout();
                    }

                    return;
                }
            }

            if (ori.shopping && ori.shopping.wizard) {
                ori.shopping.wizard.navigateNext();
            }
        };

        if (ori.shopping && ori.shopping.wizard && ori.shopping.orderSummary) {
            const $shoppingBagWrapper = ori.shopping.orderSummary.getShoppingBagWrapper();
            $shoppingBagWrapper.find(this.CHECKOUT_BUTTON_DELIVERY).on("click", handler);
        }

        // bind only once using delegate as button is re-rendered every time shopping context changes
        this.bindDelegatedClick(this.CHECKOUT_BUTTON_DELIVERY, handler);
    }

    private _handleChannelUpdate(deliveryChannelId: number, preventReloadModules?: boolean): void {
        const postData = new FormData();
        postData.append("deliveryChannelId", `${deliveryChannelId}`);

        this._fetchService.post(this._updateDeliveryChannelUrl, postData).then(
            (data: IShoppingContext) => {
                this._updateShoppingContext(data, preventReloadModules);
            },
            (error) => {
                this._apiCallFailure(error);
            }
        );
    }

    private _handleAddressUpdate(deliveryAddressId: number, preventReloadModules?: boolean): void {
        const postData = new FormData();
        postData.append("deliveryAddressId", `${deliveryAddressId}`);

        this._fetchService.post(this._updateDeliveryAddressUrl, postData).then(
            (data: IShoppingContext) => {
                this._updateShoppingContext(data, preventReloadModules);
            },
            (error) => {
                this._apiCallFailure(error);
            }
        );
    }

    private _updateShoppingContext(data: object, preventReloadModules?: boolean): void {
        if (ori.shopping && ori.shopping.wizard) {
            ori.shopping.wizard.updateShoppingContext(
                data,
                ori.shopping.wizard.deliveryPageId,
                preventReloadModules
            );
        }
    }

    private _apiCallFailure(error: string): void {
        this._logger.error("Api call failure - %s", error);
        this._disableCheckoutButton();
        ori.displayError(this._ajaxErrorTitle, this._ajaxErrorMessage);
    }

    private _getCheckoutButton(): Element | undefined {
        let element: Element | undefined = undefined;

        if (ori.shopping && ori.shopping.orderSummary) {
            const summaryBoxContext = Utils.parents(this.context, this.SHOPPING_BAG_WRAPPER);
            if (summaryBoxContext.length) {
                element = ori.shopping.orderSummary.getCheckoutButtonInSummaryBox(
                    summaryBoxContext[0]
                )[0];
            }
        }

        return element;
    }

    private _disableCheckoutButton(): void {
        this._isCheckoutButtonDisabled = true;
        Utils.addClass(this._getCheckoutButton() || "", this.DISABLED);
    }

    private _enableCheckoutButton(): void {
        this._isCheckoutButtonDisabled = false;
        Utils.removeClass(this._getCheckoutButton() || "", this.DISABLED);
    }

    private _addSpinner(): void {
        Spinner.applyOverlayTo(this.context);
    }

    private _removeSpinner(): void {
        Spinner.removeOverlayFrom(this.context);
    }

    private _updateAllChannels(dataChannels: IDeliveryModel): void {
        const {
            mySpos,
            defaultDeliveryChannel,
            multipleCouriersChannel,
            multipleCouriersToFriendChannel,
        }: IAllDeliveryChannelsData = this._prepareChannelData(dataChannels);

        const map = {
            DefaultDeliveryChannel: defaultDeliveryChannel,
            MultipleCouriersDeliveryChannel: multipleCouriersChannel,
            MultipleCouriersToFriendChannel: multipleCouriersToFriendChannel,
            SpoDeliveryChannel: mySpos,
        };

        this._channels.forEach((channel) => {
            const channelInfo = (map as any)[channel.key];
            const data = Array.isArray(channelInfo) ? channelInfo[channel.index] : channelInfo;
            const updateData: IExtendedChannelsInfo = {
                ...data,
                friendDeliveryChannel: this._deliveryToFriendComponent,
                Message: dataChannels.Message,
            };

            channel.update(updateData);
        });

        if (this._pickupPoint) {
            this._pickupPoint.update();
        }
    }

    private _isPickupPointAddressAbsent(data: IPickupPointModel | null): boolean {
        if (!data || !data.DeliveryMethods) {
            return false;
        }

        return (
            data.DeliveryMethods.length === this.SINGLE_DELIVERY_METHOD &&
            data.DeliveryMethods[0].DeliveryMethodId === this.DELIVERY_METHOD_TYPE_IS_NOT_SPECIFIED
        );
    }

    private _initAllChannels(data: IDeliveryModel): void {
        // List of channels and their initialization. Keep the order as they will appear on page - top to bottom
        const {
            mySpos,
            defaultDeliveryChannel,
            pickupPoint,
            multipleCouriersChannel,
            multipleCouriersToFriendChannel,
        }: IAllDeliveryChannelsData = this._prepareChannelData(data);

        mySpos?.forEach((mySpo) => (mySpo.Message = data.Message));
        if (defaultDeliveryChannel) {
            defaultDeliveryChannel.Message = data?.Message;
            defaultDeliveryChannel.LastUsedDeliveryAddressId = data?.LastUsedDeliveryAddressId;
        }
        if (pickupPoint) {
            pickupPoint.Message = data?.Message;
        }

        if (!mySpos && !defaultDeliveryChannel && !pickupPoint && !multipleCouriersChannel) {
            // Show "no channels available" message if there is not a single channel available
            this._showNoChannelsAvailableMessage();
            return;
        }

        const list = this.findElement(this.DELIVERY_CHANNELS_LIST);
        if (list) {
            this._initSpoDeliveryChannels(mySpos, list);
            this._initDefaultDeliveryChannel(defaultDeliveryChannel, list);
            this._initPickupPointDeliveryChannel(pickupPoint, list);

            if (data.IsDeliveryToFriendFirst && data.IsDeliveryToFriendEnabled) {
                this._initCouriersDeliveryToFriendChannel(multipleCouriersToFriendChannel, list);
            }

            if (
                multipleCouriersChannel &&
                (multipleCouriersChannel.CourierHomeDeliveryAddresses.length ||
                    multipleCouriersChannel.IsAddHomeDeliveryAddressAllowed)
            ) {
                this._initMultipleCouriersDeliveryChannel(multipleCouriersChannel, list);
            }

            if (!data.IsDeliveryToFriendFirst && data.IsDeliveryToFriendEnabled) {
                this._initCouriersDeliveryToFriendChannel(multipleCouriersToFriendChannel, list);
            }
        }
    }

    private _initSpoDeliveryChannels(data: IChannelsInfo[] | null, list: Element): void {
        if (!data) {
            return;
        }

        data.forEach((channel, index) => {
            const channelComponent = this.createComponentFromTemplateId(
                SpoDeliveryChannel,
                SpoDeliveryChannel.templateId,
                channel,
                list
            );
            channelComponent.index = index;
            this._channels.push(channelComponent);
        });
    }

    private _initDefaultDeliveryChannel(data: IChannelsInfo | null, list: Element): void {
        if (!data) {
            return;
        }
        this._channels.push(
            this.createComponentFromTemplateId(
                DefaultDeliveryChannel,
                DefaultDeliveryChannel.templateId,
                data,
                list
            )
        );
    }

    private _initPickupPointDeliveryChannel(data: IPickupPointModel | null, list: Element): void {
        if (this._isPickupPointAddressAbsent(data)) {
            return;
        }

        this._pickupPoint = this.createComponentFromTemplateId(
            PickupPointDeliveryChannel,
            PickupPointDeliveryChannel.templateId,
            data as IPickupPointModel,
            list
        );
    }

    private _initMultipleCouriersDeliveryChannel(
        data: IMultipleCouriersModel | null,
        list: Element
    ): void {
        if (!data) {
            return;
        }

        this._channels.push(
            this.createComponentFromTemplateId(
                MultipleCouriersDeliveryChannel,
                MultipleCouriersDeliveryChannel.templateId,
                data,
                list
            )
        );
    }

    private _initCouriersDeliveryToFriendChannel(
        data: IMultipleCouriersToFriendModel | null,
        list: Element
    ): void {
        if (!data) {
            this._deliveryToFriendComponent = undefined;
            return;
        }

        this._deliveryToFriendComponent = this.createComponentFromTemplateId(
            MultipleCouriersToFriendChannel,
            MultipleCouriersToFriendChannel.templateId,
            data,
            list
        );
        this._channels.push(this._deliveryToFriendComponent);
    }

    private _prepareChannelData(data: IDeliveryModel): IAllDeliveryChannelsData {
        const isDefaultDeliveryChannel = Boolean(data.DefaultDeliveryChannel);
        const SelectedDeliveryChannel = this._selected.deliveryChannel;
        const LastUsedDeliveryAddressId = data.LastUsedDeliveryAddressId;
        let isDefaultSelected = false;
        if (data.DefaultDeliveryChannel) {
            isDefaultSelected =
                data.DefaultDeliveryChannel.DeliveryChannelId === SelectedDeliveryChannel;
        }

        const {
            EndConsumersHomeDeliveryAddresses = [],
            HomeDeliveryAddresses = [],
            HomeDeliveryChannels = [],
            HomeDeliveryPhoneNumbers = null,
            IsAddHomeDeliveryAddressAllowed,
            IsDeleteHomeDeliveryAddressAllowed,
            IsEditHomeDeliveryAddressAllowed,
            Message,
            SelectedHomeDeliveryAddress,
            SelectedDeliveryPhoneId,
            AddressSearch = null,
            IsDeliveryCommentEnabled,
            IsAddEndConsumerAddressEnabled,
        }: IDeliveryModel = data;

        // MySpos
        let mySpos: IAllDeliveryChannelsData["mySpos"] = null;
        if (data.MySpos) {
            const newValues = { SelectedDeliveryChannel, isDefaultDeliveryChannel };
            mySpos = data.MySpos.map(
                (channel: IChannelsInfo) => ({ ...channel, ...newValues }) as IChannelsInfo
            );
        }

        // DefaultDeliveryChannel
        let defaultDeliveryChannel: IAllDeliveryChannelsData["defaultDeliveryChannel"] = null;
        if (data.DefaultDeliveryChannel) {
            const newValues = { SelectedDeliveryChannel, LastUsedDeliveryAddressId };
            defaultDeliveryChannel = {
                ...data.DefaultDeliveryChannel,
                ...newValues,
            } as IChannelsInfo;
        }

        // PickupPoint
        let pickupPoint = data.PickupPoint;
        if (!pickupPoint && data.DeliveryMethods && data.DeliveryMethods.length) {
            pickupPoint = {} as IPickupPointModel;
        }

        if (pickupPoint) {
            pickupPoint.provinces = data.Provinces;
            pickupPoint.allAddresses = this._deliveryHelper.getAllAddresses(HomeDeliveryAddresses);
            pickupPoint.SelectedHomeDeliveryAddress = SelectedHomeDeliveryAddress;
            pickupPoint.DeliveryMethods = data.DeliveryMethods;
            pickupPoint.mapModule = this._mapModule;
            pickupPoint.startingAddress = AddressSearch;
            pickupPoint.IsSelected = this._checkIfPickupPointIsSelected(data);
            pickupPoint.Message = data.Message;
        }

        let multipleCouriersChannel: IAllDeliveryChannelsData["multipleCouriersChannel"] = null;
        let multipleCouriersToFriendChannel: IAllDeliveryChannelsData["multipleCouriersToFriendChannel"] =
            null;

        const homeDeliveryPhoneNumbers: INotificationsPhones = {
            NotificationsPhones: HomeDeliveryPhoneNumbers,
            SelectedNotificationsPhoneId: SelectedDeliveryPhoneId
                ? SelectedDeliveryPhoneId.toString()
                : "",
        };

        // Multiple couriers
        if (HomeDeliveryChannels && HomeDeliveryChannels.length) {
            const emptyChannelObject: IExtendedChannelsInfo = {} as IExtendedChannelsInfo;
            multipleCouriersChannel = {
                ...emptyChannelObject,
                CourierHomeDeliveryAddresses: [],
                HomeDeliveryPhoneNumbers: homeDeliveryPhoneNumbers,
                IsAddHomeDeliveryAddressAllowed,
                IsDeleteHomeDeliveryAddressAllowed,
                IsDeliveryCommentEnabled,
                IsEditHomeDeliveryAddressAllowed,
                Message,
                SelectedDeliveryChannel,
                SelectedHomeDeliveryAddress,
                isDefaultDeliveryChannel,
                isDefaultSelected,
            };

            multipleCouriersToFriendChannel = {
                ...emptyChannelObject,
                EndConsumersCourierDeliveryAddresses: [],
                HomeDeliveryPhoneNumbers: homeDeliveryPhoneNumbers,
                IsAddEndConsumerAddressEnabled,
                IsDeliveryCommentEnabled,
                Message,
                SelectedDeliveryChannel,
                SelectedHomeDeliveryAddress,
                SelectedDeliveryChannelModel: data.SelectedDeliveryChannel,
                isDefaultDeliveryChannel,
                isDefaultSelected,
            };

            const multipleCouriersAddresses = this._deliveryHelper.prepareCouriersData(
                HomeDeliveryAddresses,
                HomeDeliveryChannels,
                HomeDeliveryAddressKey.HomeDeliveryAddresses
            );
            if (multipleCouriersChannel && multipleCouriersChannel.CourierHomeDeliveryAddresses) {
                multipleCouriersChannel.CourierHomeDeliveryAddresses = multipleCouriersAddresses;
            }

            const deliveryToFriendCourierAddresses = this._deliveryHelper.prepareCouriersData(
                EndConsumersHomeDeliveryAddresses,
                HomeDeliveryChannels,
                HomeDeliveryAddressKey.EndConsumersHomeDeliveryAddresses
            );
            if (
                multipleCouriersToFriendChannel &&
                multipleCouriersToFriendChannel.EndConsumersCourierDeliveryAddresses
            ) {
                multipleCouriersToFriendChannel.EndConsumersCourierDeliveryAddresses =
                    deliveryToFriendCourierAddresses;
            }
        }

        return {
            defaultDeliveryChannel,
            multipleCouriersChannel,
            multipleCouriersToFriendChannel,
            mySpos,
            pickupPoint,
        };
    }

    private _showNoChannelsAvailableMessage(): void {
        const noDeliveryAvailable = this.findElement(`.${this.NO_DELIVERY_CHANNEL_AVAILABLE}`);
        if (noDeliveryAvailable) {
            Utils.removeClass(noDeliveryAvailable, this.CLASS_HIDDEN);
        }
    }

    private _checkIfPickupPointIsSelected(DeliveryData: IDeliveryModel): Boolean {
        const pickupPoint = DeliveryData.PickupPoint;
        const mySpos = DeliveryData.MySpos;
        const defaultDeliveryChannel = DeliveryData.DefaultDeliveryChannel;
        const homeDeliveryChannels = DeliveryData.HomeDeliveryChannels;

        if (
            !pickupPoint ||
            !defaultDeliveryChannel ||
            defaultDeliveryChannel.DeliveryChannelId === pickupPoint.DeliveryChannelId
        ) {
            return false;
        }

        if (mySpos) {
            for (const mySpo of mySpos) {
                if (mySpo.DeliveryChannelId === pickupPoint.DeliveryChannelId) {
                    return false;
                }
            }
        }

        if (homeDeliveryChannels) {
            for (const homeDeliveryChannel of homeDeliveryChannels) {
                if (
                    homeDeliveryChannel.DeliveryChannelId === pickupPoint.DeliveryChannelId ||
                    this._selected.deliveryChannel === homeDeliveryChannel.DeliveryChannelId
                ) {
                    return false;
                }
            }
        }

        return true;
    }
}
